import { JobfairService } from "app/services/JobfairService";
import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";

interface JobFairState {
  loading: boolean;
  error: boolean;
  notFound: boolean;
}

const JobFairJoinComponent: React.FC = () => {
  const { alias } = useParams<{ alias: string }>();
  const history = useHistory();
  const [state, setState] = useState<JobFairState>({
    loading: true,
    error: false,
    notFound: false,
  });
  const jobfairService = new JobfairService();

  useEffect(() => {
    const fetchJobFair = async () => {
      try {
        const jobFair = await jobfairService.getJobFairByAlias(alias);
        
        if (!jobFair) {
          setState({ loading: false, error: false, notFound: true });
          return;
        }

        // Redirect to JobFair route
        history.replace(
          `/job-seeker/organizations/${jobFair.companyId}/jobfairs/${jobFair.id}`
        );
      } catch (error: any) {
        console.error("Error fetching jobfair:", error);
        
        // Check if it's a 404 error (not found)
        const is404 = error?.response?.status === 404 || 
                     error?.status === 404 || 
                     error?.message?.toLowerCase().includes('not found') ||
                     error?.code === 'NOT_FOUND';
        
        if (is404) {
          setState({ loading: false, error: false, notFound: true });
        } else {
          setState({ loading: false, error: true, notFound: false });
        }
      }
    };

    if (alias) {
      fetchJobFair();
    } else {
      setState({ loading: false, error: false, notFound: true });
    }
  }, [alias, history]);

  if (state.loading) {
    return (
      <div className="d-flex flex-center min-vh-100 bg-body">
        <div className="d-flex flex-column align-items-center">
          <div className="spinner-border text-primary mb-4" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="text-gray-600 fs-4 fw-semibold">Loading job fair...</div>
        </div>
      </div>
    );
  }

  if (state.notFound) {
    return (
      <div className="d-flex flex-center min-vh-100 bg-body">
        <div className="w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto">
          {/* Icon */}
          <div className="text-center mb-10">
            <div className="d-inline-flex align-items-center justify-content-center w-80px h-80px bg-light-danger rounded-circle mb-7">
              <span className="fs-2x text-danger" >ⓘ</span>
            </div>
            <h1 className="text-gray-900 fw-bolder fs-2x mb-4">Job Fair Not Found</h1>
            <div className="text-gray-600 fs-6 fw-semibold mb-8">
              The job fair link you're trying to access is invalid or has expired. 
              This could happen if the link was mistyped or the job fair is no longer available.
            </div>
          </div>

          {/* Support */}
          <div className="text-center pt-8 border-top border-gray-200">
            <div className="text-gray-600 fs-7 fw-semibold">
              Need help? 
              <a href="/support" className="text-primary fw-bold ms-1 text-hover-primary">
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="d-flex flex-center min-vh-100 bg-body">
        <div className="w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto">
          {/* Icon */}
          <div className="text-center mb-10">
            <div className="d-inline-flex align-items-center justify-content-center w-80px h-80px bg-light-warning rounded-circle mb-7">
              <span className="fs-2x text-danger" >ⓘ</span>
            </div>
            <h1 className="text-gray-900 fw-bolder fs-2x mb-4">Something Went Wrong</h1>
            <div className="text-gray-600 fs-6 fw-semibold mb-8">
              We encountered an error while trying to load the job fair. 
              Please check your internet connection and try again.
            </div>
          </div>

          {/* Additional Info */}
          <div className="text-center pt-8 border-top border-gray-200">
            <div className="text-gray-600 fs-7 fw-semibold">
              If the problem persists, please 
              <a href="/support" className="text-primary fw-bold ms-1 text-hover-primary">
                contact our support team
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null; // We redirect, so no UI
};

export default JobFairJoinComponent;